'use client';

import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { PaginationItem } from '@/components/ui/pagination';
import { getPaginationItems } from '@/features/products/data/pagination';
import { listingTranslate } from '@/i18n/translations/listingTranslations';

const noop = () => {};

interface ProductListPaginationProps {
  totalPages: number;
  scroll?: boolean;
}

type PaginationItemType = 'previous' | 'next' | 'page' | 'start-ellipsis' | 'end-ellipsis';

interface PaginationItemData {
  type: PaginationItemType;
  page: number | null;
  selected: boolean;
  disabled: boolean;
  'aria-current'?: 'page';
}

const getItemAriaLabel = (type: PaginationItemType, page: number | null, selected: boolean) => {
  if (type === 'page') {
    return selected
      ? `${listingTranslate('pagination.current.page')} ${page}`
      : `${listingTranslate('pagination.page')} ${page}`;
  }
  if (type === 'previous') return listingTranslate('pagination.previous');
  if (type === 'next') return listingTranslate('pagination.next');
  return listingTranslate('pagination.last');
};

export const ProductListPagination = ({
  totalPages,
  scroll = true
}: ProductListPaginationProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const activePage = Number(searchParams.get('page')) || 1;

  if (totalPages <= 1) {
    return null;
  }

  const createPageURL = (pageNumber: number) => {
    const params = new URLSearchParams(searchParams.toString());
    if (pageNumber === 1) {
      params.delete('page');
    } else {
      params.set('page', pageNumber.toString());
    }
    const query = params.toString();
    return `${pathname}${query ? `?${query}` : ''}`;
  };

  const items: PaginationItemData[] = [
    { type: 'previous', page: activePage - 1, selected: false, disabled: activePage <= 1 },
    ...getPaginationItems(activePage, totalPages).map(
      (entry, index): PaginationItemData =>
        entry === 'ellipsis'
          ? {
              type: index === 1 ? 'start-ellipsis' : 'end-ellipsis',
              page: null,
              selected: false,
              disabled: false
            }
          : {
              type: 'page',
              page: entry,
              selected: entry === activePage,
              disabled: false,
              'aria-current': entry === activePage ? 'page' : undefined
            }
    ),
    { type: 'next', page: activePage + 1, selected: false, disabled: activePage >= totalPages }
  ];

  return (
    <nav aria-label={listingTranslate('pagination.aria-label')}>
      <ul className="flex flex-nowrap items-center gap-1 p-0 m-0 list-none">
        {items.map((item, index) => {
          if (item.type.endsWith('ellipsis')) {
            return (
              <li key={index}>
                <div className="text-(--mui-palette-primary-main) flex h-9 w-6 text-[14px] items-center justify-center">
                  …
                </div>
              </li>
            );
          }

          const itemProps = {
            ...item,
            onClick: noop,
            'aria-label': getItemAriaLabel(item.type, item.page, item.selected),
            className: `text-(--mui-palette-primary-main) flex size-9 text-[14px] items-center rounded-[50%] data-selected:bg-transparent! data-selected:text-(--mui-palette-text-primary)! data-selected:border data-selected:border-(--mui-palette-action-disabled) data-selected:hover:bg-gray-50 ${
              item.disabled
                ? 'text-(--mui-palette-action-disabled)!'
                : 'text-(--mui-palette-primary-main)!'
            }`
          };

          return (
            <li key={index}>
              {/* ? disabled arrows get no href — crawlers shouldn't follow ?page=0 / ?page=last+1 */}
              {item.disabled ? (
                <PaginationItem component="a" {...itemProps} />
              ) : (
                <PaginationItem
                  component={Link}
                  href={createPageURL(item.page ?? 1)}
                  {...itemProps}
                  scroll={scroll}
                  prefetch={false}
                />
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
